import { Magnet, Trash2 } from 'lucide-react';
import FloorPlanSegmentedToggle from '../../../components/FloorPlanSegmentedToggle.jsx';

/**
 * @param {{
 *   snapToGrid: boolean;
 *   onSnapToGridChange: (value: boolean) => void;
 *   snapGridMeters: number;
 *   onSnapGridMetersChange: (value: number) => void;
 *   snapTolerancePx: number;
 *   onSnapToleranceChange: (value: number) => void;
 *   wallThicknessMeters: number;
 *   onWallThicknessChange: (value: number) => void;
 *   thicknessDirection: 'inside' | 'outside';
 *   onThicknessDirectionChange: (value: 'inside' | 'outside') => void;
 *   onClear: () => void;
 * }} props
 */
function SettingsPanel({
  snapToGrid,
  onSnapToGridChange,
  snapGridMeters,
  onSnapGridMetersChange,
  snapTolerancePx,
  onSnapToleranceChange,
  wallThicknessMeters,
  onWallThicknessChange,
  thicknessDirection,
  onThicknessDirectionChange,
  onClear,
}) {
  const clampGridMeters = (value) => {
    const rounded = Math.round((Number(value) || 0.1) * 20) / 20;
    return Math.max(0.1, Math.min(0.5, rounded));
  };

  const clampTolerancePx = (value) => {
    const parsed = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return snapTolerancePx;
    return Math.max(4, Math.min(48, parsed));
  };

  return (
    <div className="floor-plan-workspace-settings">
      <section className="floor-plan-workspace-settings-section">
        <h3 className="new-design-subheading">
          <Magnet className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
          Snap
        </h3>
        <div className="floor-plan-designer-snap-row">
          <FloorPlanSegmentedToggle
            value={snapToGrid}
            options={[
              { value: true, label: 'Snap On' },
              { value: false, label: 'Snap Off' },
            ]}
            onChange={(next) => onSnapToGridChange(!!next)}
            ariaLabel="Snap to grid"
            className="floor-plan-designer-snap-toggle-group"
          />
          <label className="floor-plan-designer-grid-value" htmlFor="workspace-snap-grid-meters">
            <span className="sr-only">Grid size in meters</span>
            <input
              id="workspace-snap-grid-meters"
              type="number"
              min="0.1"
              max="0.5"
              step="0.05"
              className="new-2d-project-input"
              value={snapGridMeters}
              disabled={!snapToGrid}
              onChange={(e) => onSnapGridMetersChange(clampGridMeters(e.target.value))}
            />
            <span>m</span>
          </label>
          <label className="floor-plan-designer-grid-value" htmlFor="workspace-snap-tolerance-px">
            <span className="sr-only">Snap tolerance in pixels</span>
            <input
              id="workspace-snap-tolerance-px"
              type="number"
              min="4"
              max="48"
              step="1"
              className="new-2d-project-input"
              value={snapTolerancePx}
              disabled={!snapToGrid}
              onChange={(e) => onSnapToleranceChange(clampTolerancePx(e.target.value))}
            />
            <span>px</span>
          </label>
        </div>
      </section>

      <section className="floor-plan-workspace-settings-section">
        <h3 className="new-design-subheading">Wall settings</h3>
        <label className="floor-plan-workspace-field">
          <span>Default thickness (m)</span>
          <input
            type="number"
            min="0.05"
            max="0.5"
            step="0.01"
            className="new-2d-project-input"
            value={wallThicknessMeters}
            onChange={(e) => onWallThicknessChange(Number(e.target.value) || wallThicknessMeters)}
          />
        </label>
        <FloorPlanSegmentedToggle
          value={thicknessDirection}
          options={[
            { value: 'inside', label: 'Inside' },
            { value: 'outside', label: 'Outside' },
          ]}
          onChange={(next) => onThicknessDirectionChange(/** @type {'inside' | 'outside'} */ (next))}
          ariaLabel="Wall thickness direction"
          className="floor-plan-designer-snap-toggle-group"
        />
      </section>

      <section className="floor-plan-workspace-settings-section">
        <h3 className="new-design-subheading">Units</h3>
        <FloorPlanSegmentedToggle
          value="m"
          options={[
            { value: 'm', label: 'm' },
            { value: 'ft', label: 'ft', disabled: true },
          ]}
          onChange={() => {}}
          ariaLabel="Measurement units"
          className="floor-plan-designer-snap-toggle-group"
        />
        <p className="floor-plan-workspace-settings-note">Feet conversion is not available yet. All values use metres.</p>
      </section>

      <section className="floor-plan-workspace-settings-section">
        <button
          type="button"
          className="btn btn-outline btn-sm floor-plan-designer-delete-all"
          onClick={onClear}
          title="Delete all"
          aria-label="Delete all"
        >
          <Trash2 size={16} aria-hidden />
          <span>Delete All</span>
        </button>
      </section>
    </div>
  );
}

export default SettingsPanel;
